import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wallet, Copy, Check, Clock, Rocket } from 'lucide-react';
import { PAYMENT_ADDRESS } from '../lib/site';

export default function PaymentModal({ open, onClose, plan }) {
  const [step, setStep] = useState(1);
  const [wallet, setWallet] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setWallet(''); setCopied(false); }
  }, [open]);

  if (!plan) return null;

  const copyAddr = async () => {
    try {
      await navigator.clipboard.writeText(PAYMENT_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  const submit = (e) => {
    e.preventDefault();
    if (wallet.trim().length < 24) return;
    setStep(2);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          onClick={onClose}
          data-testid="payment-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 0.9, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[hsl(var(--card))] border border-border rounded-3xl p-7 sm:p-8 shadow-2xl"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
              data-testid="payment-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {step === 1 ? (
              <>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))] via-cyan-400 to-purple-500 flex items-center justify-center shadow-[0_0_30px_rgba(52,200,120,0.35)] mb-5">
                    <Wallet className="w-7 h-7 text-black" />
                  </div>
                  <h2 className="font-display font-black text-3xl tracking-tight">Payment</h2>
                  <p className="text-muted-foreground mt-1.5 text-sm">
                    Plan: <span className="text-[hsl(var(--primary))] font-semibold">{plan.name}</span> — <span className="font-semibold">{plan.priceLabel}</span>
                  </p>
                </div>

                <div className="mt-7">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Send payment to</div>
                  <button
                    onClick={copyAddr}
                    className="w-full group flex items-center gap-3 p-4 rounded-xl bg-background/60 border border-border hover:border-[hsl(var(--primary))]/60 transition-colors"
                    data-testid="payment-copy-addr"
                  >
                    <span className="font-mono text-[13px] text-[hsl(var(--primary))] break-all text-left flex-1 leading-relaxed">
                      {PAYMENT_ADDRESS}
                    </span>
                    {copied
                      ? <Check className="w-5 h-5 text-[hsl(var(--primary))] shrink-0" />
                      : <Copy className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(var(--primary))] shrink-0" />
                    }
                  </button>
                </div>

                <form onSubmit={submit} className="mt-6">
                  <label className="block">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Your wallet address</div>
                    <input
                      required
                      value={wallet}
                      onChange={(e) => setWallet(e.target.value)}
                      placeholder="Enter your Solana address..."
                      className="w-full px-4 py-3.5 rounded-xl bg-background/60 border border-border focus:border-[hsl(var(--primary))]/60 focus:outline-none font-mono text-sm placeholder:text-muted-foreground/50"
                      data-testid="payment-wallet-input"
                    />
                  </label>

                  <button
                    type="submit"
                    disabled={wallet.trim().length < 24}
                    className="w-full mt-6 py-4 rounded-xl font-bold font-display text-base bg-gradient-to-r from-[hsl(var(--primary))] via-cyan-400 to-purple-500 text-black shadow-[0_0_40px_rgba(52,200,120,0.25)] hover:shadow-[0_0_60px_rgba(52,200,120,0.5)] active:scale-[0.985] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    data-testid="payment-confirm-btn"
                  >
                    Confirm
                  </button>
                </form>
              </>
            ) : (
              <div className="flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 20 }}
                  className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/40 flex items-center justify-center mb-5 shadow-[inset_0_0_24px_rgba(52,200,120,0.2)]"
                >
                  <Clock className="w-7 h-7 text-[hsl(var(--primary))]" />
                </motion.div>
                <h2 className="font-display font-black text-3xl tracking-tight">Awaiting Payment</h2>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                  Send the exact amount for <span className="text-[hsl(var(--primary))] font-semibold">{plan.name} ({plan.priceLabel})</span> to the address below:
                </p>

                <div className="w-full mt-6 space-y-3">
                  <div className="p-4 rounded-xl bg-background/60 border border-border text-left">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Destination</div>
                    <div className="font-mono text-[12.5px] text-[hsl(var(--primary))] break-all mt-1.5 leading-relaxed">{PAYMENT_ADDRESS}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-background/60 border border-border text-left">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Your wallet</div>
                    <div className="font-mono text-[12.5px] text-foreground break-all mt-1.5 leading-relaxed">{wallet}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-6">
                  <span className="relative flex w-2.5 h-2.5">
                    <span className="absolute inset-0 rounded-full bg-yellow-400 animate-ping" />
                    <span className="relative rounded-full bg-yellow-400 w-2.5 h-2.5" />
                  </span>
                  <span className="font-mono text-sm font-bold text-yellow-400">Waiting for transaction confirmation...</span>
                </div>

                <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
                  Once the network confirms your transfer (usually under 60 seconds),
                  the <span className="text-[hsl(var(--primary))] font-semibold">{plan.name}</span> plan activates automatically on your wallet.
                </p>

                <button onClick={onClose} className="btn-ghost mt-5" data-testid="payment-close-bottom">
                  <Rocket className="w-4 h-4" /> Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
